import { createAsyncThunk } from '@reduxjs/toolkit';

import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import type { OystrPayload } from '@/types/oystr/oystrTypes';
import type { CreatePartesBatchPayload, ParteApi } from '@/types/peticionamento/parteTypes';

interface FinalizarPeticionamentoPayload {
  processoId: string;
  tipoAssinatura: 'digital' | 'solicitar';
  observacoes?: string;
  oystr?: OystrPayload;
}

interface HistoricoItem {
  id: string;
  tipo: string;
  descricao: string;
  dadosNovos?: Record<string, unknown>;
  dataRegistro: string;
  usuario: {
    nome: string;
    email: string;
  };
}

interface StatusProcesso {
  id: string;
  titulo: string;
  numeroProcesso: string | null;
  numeroProtocolo: string | null;
  status: string;
  dataCriacao: string;
  dataProtocolo: string | null;
  dataFinalizacao: string | null;
}

interface CpfCnpjValidacaoResult {
  tipo: 'CPF' | 'CNPJ';
  documento: string;
  nome: string;
  valido: boolean;
}

export const finalizarPeticionamento = createAsyncThunk<
  {
    status: string;
    peticionamentoId?: string;
    numeroProtocolo?: string;
    numeroSolicitacao?: string;
    dataProtocolo?: Date;
    mensagem: string;
  },
  FinalizarPeticionamentoPayload
>('peticionamento/finalizar', async (payload, { rejectWithValue }) => {
  try {
    const { processoId, ...dados } = payload;
    const response = await apiPost(`/processos/${processoId}/ajuizar`, dados);

    if (!response.ok) {
      const error = await response.json();
      return rejectWithValue(error.message || 'Erro ao finalizar peticionamento');
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Erro ao finalizar peticionamento';
    return rejectWithValue(errorMessage);
  }
});

export const criarPartesBatch = createAsyncThunk<ParteApi[], CreatePartesBatchPayload>(
  'peticionamento/criarPartesBatch',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiPost('/partes/batch', payload);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('❌ Erro na API ao criar partes batch:', error);

        let msg = 'Erro ao criar partes';
        if (error.message) msg = error.message;
        else if (error.error) msg = error.error;
        else if (error.errors && Array.isArray(error.errors)) msg = error.errors.join(', ');
        else if (typeof error === 'string') msg = error;

        return rejectWithValue(msg);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('❌ Exceção ao criar partes batch:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao criar partes';
      return rejectWithValue(errorMessage);
    }
  },
);

export const buscarHistorico = createAsyncThunk<HistoricoItem[], string>(
  'peticionamento/buscarHistorico',
  async (processoId, { rejectWithValue }) => {
    try {
      const response = await apiGet(`/processos/${processoId}/historico`);

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || 'Erro ao buscar histórico');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao buscar histórico';
      return rejectWithValue(errorMessage);
    }
  },
);

export const buscarStatusProcesso = createAsyncThunk<StatusProcesso, string>(
  'peticionamento/buscarStatus',
  async (processoId, { rejectWithValue }) => {
    try {
      const response = await apiGet(`/processos/${processoId}`);

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || 'Erro ao buscar status');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao buscar status';
      return rejectWithValue(errorMessage);
    }
  },
);

export const validarCpfCnpjDocumento = createAsyncThunk<CpfCnpjValidacaoResult, string>(
  'peticionamento/validarCpfCnpjDocumento',
  async (documento, { rejectWithValue }) => {
    try {
      const response = await apiPost('/integracoes/cpf-cnpj/validar', { documento });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Erro ao validar CPF/CNPJ' }));
        return rejectWithValue(error.message || 'Erro ao validar CPF/CNPJ');
      }

      const result = await response.json();
      return result.data as CpfCnpjValidacaoResult;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro inesperado ao validar CPF/CNPJ';
      return rejectWithValue(errorMessage);
    }
  },
);
